import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { default as ReactDatePicker, registerLocale } from 'react-datepicker';
import { Listbox, Transition } from '@headlessui/react';
import { ReactComponent as CalendarIcon } from '../../../assets/svg/calendarIcon2.svg';
import Field from '../Field/Field';
import styles from '../DatePicker/DatePicker.module.scss';
import sv from 'date-fns/locale/sv';
import enUS from 'date-fns/locale/en-US';
import nb from 'date-fns/locale/nb';
import da from 'date-fns/locale/da';
import 'react-datepicker/dist/react-datepicker.css';
import { parseISO } from 'date-fns';

registerLocale('sv', sv);
registerLocale('en', enUS);
registerLocale('nb', nb);
registerLocale('da', da);

const SUPPORTED_LANGS = ['sv', 'en', 'nb', 'da'] as const;
type Lang = typeof SUPPORTED_LANGS[number];

const resolveLang = (raw: string | undefined): Lang => {
  const short = (raw ?? 'sv').split('-')[0].toLowerCase();
  return (SUPPORTED_LANGS as readonly string[]).includes(short) ? (short as Lang) : 'sv';
};

const buildMonths = (lang: Lang): string[] => {
  const fmt = new Intl.DateTimeFormat(lang, { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)));
};

const formatLongDate = (lang: Lang, date: Date | undefined): string => {
  if (!date) return '';
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
};

const clampDay = (year: number, month: number, day: number): number => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return Math.min(day, lastDay);
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 50 }, (_, i) => CURRENT_YEAR - 36 + i);

type CalendarSelectProps = {
  value: number,
  options: { value: number, label: string }[],
  onChange: (value: number) => void,
  wide?: boolean,
}

const CalendarSelect: FC<CalendarSelectProps> = ({ value, options, onChange, wide }) => {
  const current = options.find(o => o.value === value);
  return (
    <Listbox value={value} onChange={onChange}>
      <div className={`${styles.selectRoot} ${wide ? styles.selectWide : ''}`}>
        <Listbox.Button className={styles.selectButton}>
          <span className={styles.selectLabel}>{current?.label}</span>
          <svg className={styles.selectChevron} viewBox="0 0 10 6" aria-hidden="true">
            <path fill="currentColor" d="M5 6L0 0h10z" />
          </svg>
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter={styles.selectEnter}
          enterFrom={styles.selectEnterFrom}
          enterTo={styles.selectEnterTo}
          leave={styles.selectLeave}
          leaveFrom={styles.selectLeaveFrom}
          leaveTo={styles.selectLeaveTo}
        >
          <Listbox.Options className={styles.selectOptions}>
            {options.map(o => (
              <Listbox.Option
                key={o.value}
                value={o.value}
                className={({ active, selected }) =>
                  `${styles.selectOption} ${active ? styles.selectOptionActive : ''} ${selected ? styles.selectOptionSelected : ''}`
                }
              >
                {o.label}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

type Props = {
  selected?: Date,
  onChange: (value: Date) => void,
  label?: string,
  required?: boolean,
  errorMessage?: string,
  warningMessage?: string,
  fieldClassName?: string,
  isClearable?: boolean,
  onFocusStylesEnabled?: boolean,
  onFocusClassName?: string,
  readOnly?: boolean,
  [x:string]: any;
}

const DatePicker: FC<Props> = ({ selected, onChange, label, required, warningMessage, errorMessage, style, fieldClassName, isClearable, onFocusStylesEnabled, onFocusStylesClassName, isDisabled, readOnly, ...props }) => {
  const { i18n, t } = useTranslation('shared', { keyPrefix: 'datePicker' });
  const lang = resolveLang(i18n.language);
  const months = useMemo(() => buildMonths(lang), [lang]);

  const parsedDate = typeof selected === 'string' ? parseISO(selected) : selected;

  if (readOnly) {
    return (
      <div>
        {formatLongDate(lang, parsedDate)}
      </div>
    )
  }

  return (
    <div>
      <ReactDatePicker
        locale={lang}
        selected={parsedDate}
        onChange={onChange}
        popperProps={{ strategy: 'fixed' }}
        isClearable={!isDisabled && isClearable}
        disabled={isDisabled}
        calendarClassName={styles.calendar}
        wrapperClassName={styles.datePicker}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          const handleMonthChange = (newMonth: number) => {
            changeMonth(newMonth);
            if (!parsedDate) return;
            const y = parsedDate.getFullYear();
            const d = clampDay(y, newMonth, parsedDate.getDate());
            onChange(new Date(y, newMonth, d));
          };

          const handleYearChange = (newYear: number) => {
            changeYear(newYear);
            if (!parsedDate) return;
            const m = parsedDate.getMonth();
            const d = clampDay(newYear, m, parsedDate.getDate());
            onChange(new Date(newYear, m, d));
          };

          return (
          <div className={styles.header}>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className={styles.navButton}
              aria-label={t('previousMonth', 'Föregående månad')}
            >
              &lsaquo;
            </button>
            <CalendarSelect
              value={date.getMonth()}
              options={months.map((m, i) => ({ value: i, label: m }))}
              onChange={handleMonthChange}
              wide
            />
            <CalendarSelect
              value={date.getFullYear()}
              options={YEARS.map(y => ({ value: y, label: String(y) }))}
              onChange={handleYearChange}
            />
            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className={styles.navButton}
              aria-label={t('nextMonth', 'Nästa månad')}
            >
              &rsaquo;
            </button>
          </div>
          );
        }}
        customInput={(
          <div>
            <Field
              label={label}
              required={required}
              warningMessage={warningMessage}
              errorMessage={errorMessage}
              onFocusStylesEnabled={onFocusStylesEnabled}
              onFocusClassName={onFocusStylesClassName}
              disabled={isDisabled}
              icon={<CalendarIcon className={styles.datePickerIcon} width='18px' />}
              value={formatLongDate(lang, parsedDate)}
              style={style}
              className={`${fieldClassName} ${isClearable ? styles.isClearable : ''}`}
            />
          </div>
        )}
        popperPlacement='bottom-end'
        showWeekNumbers
        {...props}
      />
    </div>
  );
}

export default DatePicker;
